import React, { useEffect, useState } from 'react';
import { Button, Typography, Tag, Row, Col, Form } from 'antd';
import {
  GlobalOutlined,
  ReloadOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { getClassificationService } from 'libs/p42/classification/factory';
import { UiStage } from '@xbcb/ui-types';
import { useCurrentUser } from 'libs/hooks';
import LoadingSpinner from 'components/LoadingSpinner';

const { Text, Paragraph } = Typography;

interface AIDrawerProps {
  productDescription: string;
  manufacturerName: string;
  countryOfOrigin: string;
  fullNamePath?: (string | number)[];
  onAccept?: () => void;
}

const AIDrawer: React.FC<AIDrawerProps> = ({
  productDescription,
  manufacturerName,
  countryOfOrigin,
  fullNamePath,
  onAccept,
}) => {
  const [questions, setQuestions] = useState<any[]>([]);
  const [classificationId, setClassificationId] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [processingAnswers, setProcessingAnswers] = useState(false);
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [htsNumber, setHtsNumber] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [reasoningExpanded, setReasoningExpanded] = useState(true);
  const currentUser = useCurrentUser();
  const shipperId = currentUser.account?.id;
  const service = getClassificationService(UiStage.GAMMA, shipperId);
  const form = Form.useFormInstance();

  useEffect(() => {
    try {
      setLoading(true);
      const res = service.startInteractiveClassification({
        productDescription,
        manufacturerName,
        countryOfOrigin,
      });
      setClassificationId(res.id);
      setQuestions(res.questions || []);
    } catch (err) {
      console.error('Failed to start classification session:', err);
    } finally {
      setLoading(false);
    }
  }, [productDescription, manufacturerName, countryOfOrigin]);

  const handleSelect = (questionId: string, optionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = () => {
    try {
      const formattedAnswers = Object.entries(answers).map(
        ([questionId, answerId]) => ({ questionId, answerId }),
      );
      const res = service.submitClassificationAnswers({
        classificationId,
        answers: formattedAnswers,
      });

      setProcessingAnswers(true);
      setTimeout(async () => {
        setProcessingAnswers(false);
        setHtsNumber(res.result?.htsNumber || '');
        setReasoning(res.result?.reasoning || '');
        setShowMessageBox(true);
      }, 4000);
    } catch (err) {
      console.error('Failed to submit answers:', err);
    }
  };

  const handleRestart = () => {
    window.location.reload();
  };

  const handleAccept = () => {
    if (htsNumber && fullNamePath) {
      form.setFieldValue([...fullNamePath, 'tariffs'], [{ htsNumber }]);
      if (onAccept) {
        onAccept();
      }
    }
  };

  return (
    <>
      <div
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          padding: 18,
          backgroundColor: '#fff',
          maxWidth: 1000,
        }}
      >
        {loading && <p>Loading classification questions...</p>}
        {!loading &&
          questions.map(({ id, text, options }) => (
            <div key={id} style={{ marginBottom: 30 }}>
              <div style={{ display: 'flex' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    borderRadius: '50%',
                    backgroundColor: '#1890ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 5,
                  }}
                >
                  <GlobalOutlined style={{ color: '#fff', fontSize: 20 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      backgroundColor: '#e6f4ff',
                      padding: '8px 12px',
                      borderRadius: 4,
                      marginBottom: 10,
                    }}
                  >
                    <Text strong>{text}</Text>
                  </div>
                  <Row gutter={[8, 8]} wrap>
                    {options.map((opt: any) => {
                      const isSelected = answers[id] === opt.id;
                      return (
                        <Col key={opt.id}>
                          <Tag
                            onClick={() => handleSelect(id, opt.id)}
                            style={{
                              borderRadius: 5,
                              padding: '4px 12px',
                              fontSize: 14,
                              cursor: 'pointer',
                              userSelect: 'none',
                              backgroundColor: isSelected ? '#1890ff' : '#fff',
                              color: isSelected ? '#fff' : '#000',
                              border: isSelected
                                ? '1px solid #1890ff'
                                : '1px solid #d9d9d9',
                            }}
                          >
                            {opt.text}
                          </Tag>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              </div>
            </div>
          ))}
        {!processingAnswers && !showMessageBox && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 5,
            }}
          >
            <Button
              type="default"
              onClick={handleSubmit}
              style={{
                borderColor: '#1890ff',
                color: '#1890ff',
                backgroundColor: '#fff',
                fontWeight: 900,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1890ff';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#1890ff';
              }}
            >
              Submit answers for analysis
            </Button>
          </div>
        )}
        {processingAnswers && <LoadingSpinner />}
        {showMessageBox && (
          <div style={{ display: 'flex', marginTop: 24 }}>
            <div
              style={{
                width: 32,
                height: 32,
                minWidth: 32,
                borderRadius: '50%',
                backgroundColor: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 5,
              }}
            >
              <GlobalOutlined style={{ color: '#fff', fontSize: 20 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  padding: 12,
                  backgroundColor: '#e6f4ff',
                  border: '1px solid #91d5ff',
                  borderRadius: 6,
                }}
              >
                <Paragraph style={{ marginBottom: 12 }}>
                  <Text strong>
                    Based on your input, here are the details about your
                    product:
                  </Text>
                </Paragraph>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li style={{ marginBottom: 4 }}>
                    <Text strong>HTS Code:</Text> {htsNumber || 'N/A'}
                  </li>
                </ul>

                {reasoning && (
                  <div style={{ marginTop: 12 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderTop: '1px solid #c8dbea',
                        paddingTop: 12,
                      }}
                      onClick={() => setReasoningExpanded(!reasoningExpanded)}
                    >
                      <Text strong style={{ color: '#000' }}>
                        Hide reasoning
                      </Text>
                      <Button
                        type="text"
                        icon={
                          reasoningExpanded ? (
                            <MinusOutlined />
                          ) : (
                            <PlusOutlined />
                          )
                        }
                        style={{ padding: 0, height: 'auto', marginLeft: 8 }}
                      />
                    </div>
                    {reasoningExpanded && (
                      <Paragraph style={{ marginTop: 12, fontSize: 13 }}>
                        <Text>{reasoning}</Text>
                      </Paragraph>
                    )}
                  </div>
                )}
              </div>
              <div
                style={{
                  marginTop: 24,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: 12,
                }}
              >
                <Button
                  type="default"
                  style={{ fontWeight: 500, minWidth: 100 }}
                  onClick={handleAccept}
                >
                  Accept
                </Button>
                <Button
                  type="default"
                  style={{ fontWeight: 500, minWidth: 100 }}
                >
                  Decline
                </Button>
              </div>
            </div>
          </div>
        )}
        {showMessageBox && (
          <div
            style={{
              marginTop: 24,
              paddingTop: 12,
              borderTop: '1px solid #d9d9d9',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              type="link"
              icon={<ReloadOutlined />}
              onClick={handleRestart}
              style={{ fontSize: 14 }}
            >
              Restart classification
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default AIDrawer;
